'use client';

import { useState, useCallback, useEffect, useRef, RefObject } from 'react';

/**
 * Custom hook to handle focus effects for both mouse and touch interactions
 * 
 * @param options Configuration options for the hook
 * @returns An object containing the focus state, event handlers, and ref
 */
export interface UseFocusEffectOptions {
  /** Delay in ms before removing focus after touch end (default: 200) */
  touchEndDelay?: number;
  /** Whether to enable touch events (default: true) */
  enableTouch?: boolean;
  /** Whether to enable mouse events (default: true) */
  enableMouse?: boolean;
  /** Whether to enable keyboard focus events (default: true) */
  enableKeyboard?: boolean;
  /** Initial focus state (default: false) */
  initialFocus?: boolean;
}

export interface UseFocusEffectResult<T extends HTMLElement> {
  /** Whether the element is currently focused */
  isFocused: boolean;
  /** Ref to attach to the element */
  ref: RefObject<T>;
  /** Event handler for mouse enter or touch start */
  handleFocusStart: () => void;
  /** Event handler for mouse leave or touch end */
  handleFocusEnd: () => void;
  /** Props to spread on the element */
  focusProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
    tabIndex: number;
  };
}

/**
 * Custom hook to handle focus effects for both mouse and touch interactions
 * 
 * @example
 * ```tsx
 * const { isFocused, ref, focusProps } = useFocusEffect<HTMLDivElement>();
 * 
 * return (
 *   <div 
 *     ref={ref}
 *     className={cn(isFocused ? 'focused-styles' : 'normal-styles')}
 *     {...focusProps}
 *   >
 *     Content
 *   </div>
 * );
 * ```
 */
export function useFocusEffect<T extends HTMLElement>(
  options: UseFocusEffectOptions = {}
): UseFocusEffectResult<T> {
  const {
    touchEndDelay = 200,
    enableTouch = true,
    enableMouse = true,
    enableKeyboard = true,
    initialFocus = false,
  } = options;
  
  const [isFocused, setIsFocused] = useState(initialFocus);
  const ref = useRef<T>(null);
  
  const handleFocusStart = useCallback(() => {
    setIsFocused(true);
  }, []);
  
  const handleFocusEnd = useCallback(() => {
    setIsFocused(false);
  }, []);
  
  // Handle touch events for mobile/tablet
  useEffect(() => {
    if (!enableTouch) return;
    
    const handleTouchStart = (e: TouchEvent) => {
      // Only set focus if the touch is on this element
      if (e.target instanceof Node && ref.current?.contains(e.target)) {
        setIsFocused(true);
      } else {
        // If touch is outside, remove focus
        setIsFocused(false);
      }
    };
    
    const handleTouchEnd = () => {
      // Use a timeout to allow the click event to fire before removing focus
      setTimeout(() => setIsFocused(false), touchEndDelay);
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enableTouch, touchEndDelay]);
  
  // Create props object for the element
  const focusProps = {
    onMouseEnter: enableMouse ? handleFocusStart : () => {},
    onMouseLeave: enableMouse ? handleFocusEnd : () => {},
    onFocus: enableKeyboard ? handleFocusStart : () => {},
    onBlur: enableKeyboard ? handleFocusEnd : () => {},
    tabIndex: enableKeyboard ? 0 : -1,
  };
  
  return {
    isFocused,
    ref: ref as RefObject<T>,
    handleFocusStart,
    handleFocusEnd,
    focusProps,
  };
} 